import { Button } from '@civilgrid/ui/components';
import { useState } from 'react';
import { type SubmitHandler, type UseFormReturn, useForm } from 'react-hook-form';
import {
  computeIntelligence as apiComputeIntelligence,
  syncIntelligenceForOrg as apiSyncForOrg,
  syncIntelligence as apiSyncIntelligence,
} from '~/api/internal';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import { MajorHeading } from '../common/Heading';

const useRunQuery = ({
  query,
  title,
  message = 'Job started',
  form,
}: {
  title: string;
  query: any;
  message?: string;
  form?: UseFormReturn<TFormValues, any>;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const onSubmit = async () => {
    try {
      setIsProcessing(true);

      const res = await query();
      if (!res.success) throw new Error('Bad response!');

      Notify.info({
        title,
        message,
      });

      form?.reset({});
    } catch (e) {
      console.error(`Error running ${title}: `, e);
      Notify.error({
        title,
        message: `An error occurred attempting to run ${title}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return { isProcessing, onSubmit };
};

type TFormValues = { id: string };

export default function IntelligenceTools() {
  const orgForm = useForm<TFormValues>({ defaultValues: { id: '' } });
  const projectForm = useForm<TFormValues>({ defaultValues: { id: '' } });

  const orgId = orgForm.watch('id');
  const projectId = projectForm.watch('id');

  const sync = useRunQuery({
    query: apiSyncIntelligence,
    title: 'Sync Intelligence',
    message: 'Sync Intelligence started for ALL organizations',
  });

  const syncByOrg = useRunQuery({
    query: () => apiSyncForOrg(orgId),
    title: 'Sync Intelligence for Organization',
    message: `Sync Intelligence started for organization ${orgId}`,
    form: orgForm,
  });

  const compute = useRunQuery({
    query: () => apiComputeIntelligence(projectId),
    title: 'Compute Intelligence for Project',
    message: `Compute Intelligence started for project ${projectId}`,
    form: projectForm,
  });

  const onOrgSubmit: SubmitHandler<TFormValues> = (data) => {
    syncByOrg.onSubmit();
  };

  const onProjectSubmit: SubmitHandler<TFormValues> = (data) => {
    compute.onSubmit();
  };

  return (
    <div className="flex flex-col items-start space-y-4 mb-8">
      <div>
        <MajorHeading tooltip="Run this query to sync all active Projects' intelligence with ESRI. NOTE: This will also sync undigitized agencies.">
          Sync Intelligence
        </MajorHeading>
        <Button onClick={sync.onSubmit} loading={sync.isProcessing} disabled={sync.isProcessing}>
          Sync Intelligence
        </Button>
      </div>

      <div>
        <MajorHeading tooltip="Run this query to sync intelligence for a specific organization">
          Sync Intelligence for Org
        </MajorHeading>

        <Form onSubmit={orgForm.handleSubmit(onOrgSubmit)} className="space-y-4">
          <ControlledInput control={orgForm.control} isRequired name={'id'} label="24-digit Organization ID" />

          <Button type="submit" disabled={!orgForm.formState.isValid || syncByOrg.isProcessing}>
            Sync Intelligence for Org
          </Button>
        </Form>
      </div>

      <div>
        <MajorHeading tooltip="Run this query for a specific project to compute project intelligence. Enter the database ID.">
          Compute Intelligence for Project
        </MajorHeading>

        <Form onSubmit={projectForm.handleSubmit(onProjectSubmit)} className="space-y-4">
          <ControlledInput control={projectForm.control} name={'id'} isRequired label="24-digit Project ID" />

          <Button type="submit" disabled={!projectForm.formState.isValid || compute.isProcessing}>
            Compute Intelligence for Project
          </Button>
        </Form>
      </div>
    </div>
  );
}
